import { FormContentFieldProps } from '@common-ui-library/shared';

export enum UserManagementEntityType {
  SITES = 'sites',
  PROVIDERS = 'providers',
}

export enum ValueField {
  DEFAULT_VALUE = 'defaultValue',
  VALUE = 'value',
  CHECKED = 'checked',
}

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'DELETE';

export type EntityNameMappingType = {
  [key in UserManagementEntityType]: string;
};

// =============================================================================
// Entity Types
// =============================================================================

/** Role definition for user permissions */
export interface Role {
  roleId: string;
  hasAccess: boolean;
}

/** Provider entity - represents a user/provider in the system */
export interface Provider {
  id: string;
  name: string;
  email: string;
  phone?: string;
  profession?: string;
  site?: { id: string; name: string }[];
  supervisor?: string;
  program?: string;
  roles?: Role[];
  is_excluded_metrics?: boolean;
  owner?: {
    account_status: 'active' | 'deactivated';
  };
  selected?: boolean; // UI state for table selection
}

/** Site entity - represents a location/site in the system */
export interface Site {
  id: string;
  name: string;
  organization?: string;
  timezone?: string;
  selected?: boolean; // UI state for table selection
}

/** Union type for all entity types */
export type UserManagementEntity = Provider | Site;

/** Type guard to check if entity is a Provider */
export const isProvider = (item: UserManagementEntity | null): item is Provider => {
  return item !== null && 'email' in item;
};

/** Generic API response wrapper */
export interface ApiResponse<T> {
  status: number;
  data: {
    results?: T[];
    pagination?: Pagination;
  };
}

/** Dropdown option type for select fields */
export interface DropdownOption {
  value: string;
  label: string;
}

// =============================================================================
// State Types
// =============================================================================

export interface UserManagementState {
  userManagementEntity: UserManagementEntityType | undefined;
  items: UserManagementEntity[];
  selectedItem: UserManagementEntity | null;
  isAddEntityOpen: boolean;
  pagination?: Pagination;
  isLoading: boolean;
  createUpdateError: { status: boolean; message: string };
}

export type RequestFields = {
  entityId?: string;
  ordering?: string;
  page?: string;
  rowsPerPage?: string;
  search?: string;
  filters?: string[];
};

export interface FieldValidations {
  [key: string]: string;
}

export interface FormContentFieldPropsWithValidation
  extends FormContentFieldProps {
  validationFunction?: (value: any) => boolean;
  isRequired?: boolean;
}

export interface DetailsCardProps {
  userManagementEntity?: string;
  cardHeader?: React.ReactNode;
  initialData: UserManagementEntity | null;
  onExited: () => void;
}

export interface CardHeaderProps {
  name: string;
  roles?: Role[];
  email: string;
}

export interface AddNewEntityModalProps {
  open: boolean;
  userManagementEntity?: string;
}

export interface Pagination {
  total_count?: number;
  page_size?: number;
  current_page?: number;
  total_pages?: number;
  next?: string;
  previous?: string;
}

export interface UserManagementContextType extends UserManagementState {
  // Handlers
  handleRowClick: (item: UserManagementEntity) => void;
  onExited: () => void;
  onPageChange: (page: number, rowsPerPage: number) => Promise<void>;
  onSorted: (key: string, direction: -1 | 1) => Promise<void>;
  onSearchChange: (search: string) => Promise<void>;
  onFilterChange: (filters: string[] | null) => void;

  // Actions
  onSaveItem: (item: Partial<UserManagementEntity>, create: boolean) => Promise<boolean>;
  onDeactivateItem: (item: UserManagementEntity) => Promise<boolean>;
  setIsAddEntityOpen: (isAddEntityOpen: boolean) => void;
  getDropdownOptions: () => Promise<{ status: number; data: Record<string, DropdownOption[]> }>;
}

export interface ActionButtonsProps {
  onSubmit: () => void;
  onDeactivate: () => void;
  onReactivate?: () => void;
  entity: string;
  disabled?: boolean;
  isDeactivated?: boolean;
}

export type EntityActions = {
  update?: string;
  deactivate?: string;
};

export type FieldTextMap = {
  [K in 'sites' | 'providers']: EntityActions;
};
